import React, { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  LayoutDashboard, FileText, User, LogOut, Users, Package,
  CreditCard, Sparkles, Plus, Receipt, Contact, BarChart3, Megaphone, Activity, Lightbulb, ChevronDown, Hotel,
} from "lucide-react";

const NavItem = ({ to, icon: Icon, label, testid }) => (
  <NavLink
    to={to}
    end
    data-testid={testid}
    className={({ isActive }) =>
      `group flex items-center gap-3 px-3 py-2.5 rounded text-sm transition-colors ${
        isActive
          ? "bg-[#16A34A] text-white"
          : "text-slate-300 hover:bg-white/5 hover:text-white"
      }`
    }
  >
    <Icon size={16} strokeWidth={1.6} />
    <span>{label}</span>
  </NavLink>
);

function ProfileMenu({ role }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const email = user?.email || "";
  const initial = (email[0] || "?").toUpperCase();
  const label = email.split("@")[0] || "Account";

  return (
    <div ref={ref} className="fixed top-3 right-3 md:top-4 md:right-4 z-50">
      <button
        data-testid="profile-menu-trigger"
        onClick={() => setOpen((v) => !v)}
        title={email || "Account"}
        className="w-10 h-10 rounded-full bg-[#0F1E5B] text-white text-sm font-semibold flex items-center justify-center shadow-sm hover:ring-2 hover:ring-[#16A34A] transition"
      >
        {initial}
      </button>
      {open && (
        <div
          data-testid="profile-menu"
          className="absolute right-0 mt-2 w-64 bg-white border border-slate-200 rounded-md shadow-lg overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-slate-100">
            <div className="text-[10px] uppercase tracking-widest text-slate-400">Signed in as</div>
            <div className="text-sm font-medium text-slate-900 truncate" title={email}>{email}</div>
            {user?.agent_id && (
              <div className="text-[11px] font-mono-e text-emerald-700 mt-0.5">{user.agent_id}</div>
            )}
          </div>
          {role !== "admin" && (
            <button
              data-testid="profile-menu-profile"
              onClick={() => { setOpen(false); nav("/profile/setup"); }}
              className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
            >
              <User size={14} /> Profile
            </button>
          )}
          <button
            data-testid="logout-btn"
            onClick={() => { setOpen(false); logout(); nav("/"); }}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 border-t border-slate-100"
          >
            <LogOut size={14} /> Log out
          </button>
        </div>
      )}
    </div>
  );
}

export default function Sidebar({ role = "agent" }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  return (
    <>
      {/* Top-right profile dropdown — visible on every authenticated page */}
      <ProfileMenu role={role} />

      <aside
        data-testid="sidebar"
        className="w-64 bg-[#0F1E5B] text-white min-h-screen flex flex-col noise-bg relative"
      >
      <div className="p-4 border-b border-white/10 bg-white">
        <img
          src="/brand/logo.png"
          alt="Traveloble"
          className="w-full h-auto max-h-40 object-contain select-none"
          draggable="false"
        />
      </div>

      <nav className="flex-1 p-4 space-y-1">
        {role === "admin" ? (
          <>
            <NavItem to="/admin" icon={LayoutDashboard} label="Overview" testid="nav-admin-overview" />
            <NavItem to="/admin/agents" icon={Users} label="Travel Agents" testid="nav-admin-agents" />
            <NavItem to="/admin/activity" icon={Activity} label="Agent Activity" testid="nav-admin-activity" />
            <NavItem to="/admin/suggestions" icon={Lightbulb} label="Suggestions" testid="nav-admin-suggestions" />
            <NavItem to="/admin/hotel-categories" icon={Hotel} label="Hotel Categories" testid="nav-admin-hotel-cats" />
            <NavItem to="/admin/subscriptions" icon={Package} label="Subscriptions" testid="nav-admin-subs" />
          </>
        ) : (
          <>
            <NavItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" testid="nav-dashboard" />
            <NavItem to="/customers" icon={Contact} label="Customers" testid="nav-customers" />
            <NavItem to="/quotations" icon={FileText} label="Quotations" testid="nav-quotations" />
            <NavItem to="/vouchers" icon={CreditCard} label="Hotel Vouchers" testid="nav-vouchers" />
            <NavItem to="/invoices" icon={Receipt} label="Invoices" testid="nav-invoices" />
            <NavItem to="/pamphlets" icon={Megaphone} label="Pamphlets" testid="nav-pamphlets" />
            <NavItem to="/reports" icon={BarChart3} label="Reports" testid="nav-reports" />
            <NavItem to="/suggestions" icon={Lightbulb} label="Suggestions" testid="nav-suggestions" />
            <NavItem to="/profile/setup" icon={User} label="Profile" testid="nav-profile" />
            <button
              onClick={() => nav("/quotations/templates")}
              data-testid="sidebar-new-quotation-btn"
              className="w-full mt-6 btn-bronze flex items-center justify-center gap-2 text-sm"
            >
              <Plus size={15} /> New Quotation
            </button>
          </>
        )}
      </nav>

      <div className="p-4 border-t border-white/10">
        <div className="text-xs">
          <div className="text-white/60 tracking-wide">SIGNED IN AS</div>
          <div className="text-white font-medium truncate">{user?.email}</div>
          {user?.agent_id && (
            <div className="text-[#BBF7D0] font-mono-e text-[11px] mt-0.5">
              {user.agent_id}
            </div>
          )}
        </div>
      </div>
    </aside>
    </>
  );
}
